import { useEffect, useState } from 'react';

import { createAdminApi } from '@/admin/adminApi';
import { isForbidden, messageOf } from '@/admin/users/adminErrors';
import { useHttpClient } from '@/app/providers';

import {
  ACCOUNT_RESULT_LIMIT,
  isSearchable,
  searchTermOf,
  SEARCH_DELAY_MS,
  type AccountSearchState,
} from './paletteModel';

/**
 * Recherche de comptes de la palette : `GET /admin/users?search=`.
 *
 * Trois regles, toutes dictees par l'audit (une ligne par appel) :
 *
 * - **rien ne part sous 3 caracteres** ;
 * - **une requete par saisie stabilisee** : la minuterie est relancee a chaque
 *   frappe, seule la derniere aboutit a un appel ;
 * - **une reponse perimee est ignoree** : le nettoyage de l'effet l'invalide, et
 *   l'issue rangee porte la clef de la demande qui l'a produite — elle ne
 *   s'affiche que pour cette saisie.
 *
 * Un 403 est **retenu** : le role ne change pas pendant qu'on tape, et
 * re-demander ne produirait qu'un refus de plus. Il ne deconnecte jamais
 * (ADR 0032). Il est retenu **par l'appelant** (`forbidden`, `onForbidden`) : ce
 * hook vit avec l'ouverture de la palette, et tout ce qu'il garde — resultats
 * compris — disparait a la fermeture.
 */

interface Settled {
  readonly key: string;
  readonly outcome: Extract<AccountSearchState, { kind: 'ready' | 'error' }>;
}

export interface AccountSearch {
  readonly state: AccountSearchState;
  readonly retry: () => void;
}

export const useAccountSearch = (query: string, forbidden: boolean, onForbidden: () => void): AccountSearch => {
  const http = useHttpClient();
  const term = searchTermOf(query);
  const searchable = isSearchable(query);
  const [attempt, setAttempt] = useState(0);
  const [settled, setSettled] = useState<Settled | null>(null);
  const key = `${String(attempt)}:${term}`;

  useEffect(() => {
    if (!searchable || forbidden) {
      return undefined;
    }

    let cancelled = false;

    const load = async (): Promise<void> => {
      try {
        const page = await createAdminApi(http).listUsers(term, 1, ACCOUNT_RESULT_LIMIT);

        if (!cancelled) {
          setSettled({ key, outcome: { kind: 'ready', users: page.items, total: page.total } });
        }
      } catch (error: unknown) {
        if (cancelled) {
          return;
        }

        if (isForbidden(error)) {
          onForbidden();
        } else {
          setSettled({ key, outcome: { kind: 'error', message: messageOf(error) } });
        }
      }
    };

    const timer = setTimeout(() => {
      void load();
    }, SEARCH_DELAY_MS);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [http, term, key, searchable, forbidden, onForbidden]);

  const retry = (): void => {
    setAttempt((previous) => previous + 1);
  };

  // L'attente est **deduite** de la clef, jamais ecrite dans l'effet (meme
  // technique que `useAdminAccess`).
  let state: AccountSearchState;

  if (!searchable) {
    state = { kind: 'idle' };
  } else if (forbidden) {
    state = { kind: 'forbidden' };
  } else if (settled !== null && settled.key === key) {
    state = settled.outcome;
  } else {
    state = { kind: 'loading' };
  }

  return { state, retry };
};
